import { useEffect, useRef, useState } from 'react';
import Peer from 'peerjs';

const API_HOST = process.env.REACT_APP_API_URL
    ? new URL(process.env.REACT_APP_API_URL).hostname
    : 'localhost';

const IS_PROD = API_HOST !== 'localhost';

export default function RemoteSession({ sessionId, role, onEnd, ticketType }) {
    const [status, setStatus] = useState('connecting');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const videoRef = useRef(null);
    const peerRef = useRef(null);
    const connRef = useRef(null);
    const streamRef = useRef(null);
    const [elapsed, setElapsed] = useState(0);
    const startedAt = useRef(null);
    const containerRef = useRef(null);

    const isExpress = ticketType === 'express';
    const BASE_COST = isExpress ? 29 : 20;
    const RATE_PER_MIN = 0.75;
    const EXPRESS_INCLUDED_MIN = 15;
    const STANDARD_INCLUDED_MIN = 10;

    useEffect(() => {
        if (status !== 'streaming' && status !== 'connected') return;
        if (!startedAt.current) startedAt.current = Date.now();
        const timer = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
        }, 1000);
        return () => clearInterval(timer);
    }, [status]);

    useEffect(() => {
        let destroyed = false;
        let timer = null;

        const peerId = role === 'client' ? `client-${sessionId}` : `tech-${sessionId}`;

        const peerConfig = IS_PROD
            ? { host: API_HOST, port: 443, path: '/peerjs', secure: true }
            : { host: 'localhost', port: 3001, path: '/peerjs' };

        const p = new Peer(peerId, peerConfig);
        peerRef.current = p;

        p.on('open', () => {
            setStatus('waiting');

            if (role === 'client') {
                p.on('connection', (conn) => {
                    conn.on('data', (data) => {
                        setMessages(m => [...m, { from: 'technicien', text: data }]);
                    });
                });

                const outConn = p.connect(`tech-${sessionId}`);
                connRef.current = outConn;
                outConn.on('error', () => { });

                navigator.mediaDevices.getDisplayMedia({ video: true })
                    .then((stream) => {
                        if (destroyed) return;
                        streamRef.current = stream;

                        stream.getVideoTracks()[0].onended = () => {
                            setStatus('screen_denied');
                        };

                        p.on('call', (call) => {
                            call.answer(stream);
                        });
                        setTimeout(() => {
                            if (destroyed) return;
                            const call = p.call(`tech-${sessionId}`, stream);
                            if (call) call.on('stream', () => { });
                            setStatus('connected');
                        }, 2000);
                    })
                    .catch(() => setStatus('screen_denied'));
            }

            if (role === 'technicien') {
                p.on('call', (call) => {
                    call.answer();
                    call.on('stream', (remoteStream) => {
                        if (videoRef.current && !destroyed) {
                            videoRef.current.srcObject = remoteStream;
                            videoRef.current.play();
                            setStatus('streaming');
                        }
                    });
                });

                p.on('connection', (conn) => {
                    conn.on('data', (data) => {
                        setMessages(m => [...m, { from: 'client', text: data }]);
                    });
                });

                timer = setTimeout(() => {
                    if (destroyed) return;
                    const conn = p.connect(`client-${sessionId}`);
                    connRef.current = conn;
                    conn.on('error', () => { });
                }, 3000);
            }
        });

        p.on('error', (err) => {
            if (err.type !== 'peer-unavailable') {
                console.error('PeerJS error:', err);
                setStatus('error');
            }
        });

        return () => {
            destroyed = true;
            if (timer) clearTimeout(timer);
            streamRef.current?.getTracks().forEach(t => t.stop());
            try { p.destroy(); } catch (e) { }
        };
    }, [sessionId, role]);

    const sendMessage = () => {
        if (!input.trim() || !connRef.current) return;
        connRef.current.send(input);
        setMessages(m => [...m, { from: role, text: input }]);
        setInput('');
    };

    const formatElapsed = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const estimatedCost = () => {
        const minutes = elapsed / 60;
        if (isExpress) {
            if (minutes <= EXPRESS_INCLUDED_MIN) return BASE_COST.toFixed(2);
            return (BASE_COST + (minutes - EXPRESS_INCLUDED_MIN) * RATE_PER_MIN).toFixed(2);
        }
        if (minutes <= STANDARD_INCLUDED_MIN) return BASE_COST.toFixed(2);
        return (BASE_COST + (minutes - STANDARD_INCLUDED_MIN) * RATE_PER_MIN).toFixed(2);
    };

    const costLabel = () => {
        const minutes = elapsed / 60;
        if (isExpress && minutes <= EXPRESS_INCLUDED_MIN) {
            return `Fix Express — ${Math.ceil(EXPRESS_INCLUDED_MIN - minutes)} min incluses restantes`;
        }
        if (!isExpress && minutes <= STANDARD_INCLUDED_MIN) {
            return `${Math.ceil(STANDARD_INCLUDED_MIN - minutes)} min incluses restantes`;
        }
        return `0,75€/min en cours`;
    };

    const statusText = () => {
        if (role === 'technicien') {
            if (status === 'connecting') return '⏳ Connexion au serveur...';
            if (status === 'waiting' || status === 'ready') return '⏳ En attente du client...';
            if (status === 'streaming') return '🟢 Écran du client visible';
            if (status === 'error') return '🔴 Erreur de connexion';
        }
        if (role === 'client') {
            if (status === 'connecting') return '⏳ Connexion...';
            if (status === 'waiting') return '⏳ En attente du technicien...';
            if (status === 'connected') return '🟢 Écran partagé';
            if (status === 'screen_denied') return "⚠️ Partage d'écran refusé";
            if (status === 'error') return '🔴 Erreur de connexion';
        }
        return '...';
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    return (
        <div ref={containerRef} style={{
            display: 'flex', gap: 12, marginTop: 16,
            background: '#111', border: `1px solid ${isExpress ? 'rgba(74,222,128,0.2)' : '#2a2a2a'}`,
            borderRadius: 12, padding: 16
        }}>
            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: status === 'streaming' || status === 'connected' ? '#4caf50' : '#f59e0b' }}>
                            {statusText()}
                        </span>
                        {isExpress && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80' }}>
                                ⚡ EXPRESS
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        {role === 'technicien' && (
                            <button onClick={toggleFullscreen} style={{
                                background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none',
                                borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12
                            }}>
                                ⛶ Plein écran
                            </button>
                        )}
                        <button onClick={onEnd} style={{
                            background: '#ef4444', color: '#fff', border: 'none',
                            borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12
                        }}>
                            Terminer
                        </button>
                    </div>
                </div>

                {/* Timer + coût */}
                <div style={{
                    display: 'flex', gap: 16, marginBottom: 10,
                    background: '#1a1a1a', borderRadius: 8, padding: '8px 12px',
                    fontSize: 13, alignItems: 'center', flexWrap: 'wrap'
                }}>
                    <span style={{ color: '#aaa' }}>⏱ <strong style={{ color: '#fff' }}>{formatElapsed(elapsed)}</strong></span>
                    <span style={{ color: '#aaa' }}>💶 <strong style={{ color: isExpress ? '#4ade80' : '#4caf50' }}>{estimatedCost()}€</strong></span>
                    <span style={{ color: '#555', fontSize: 11 }}>({costLabel()})</span>
                </div>

                {role === 'technicien' && (
                    <video ref={videoRef} autoPlay style={{
                        width: '100%', borderRadius: 8,
                        background: '#000', minHeight: 250
                    }} />
                )}

                {role === 'client' && (
                    <div style={{ background: '#1a1a1a', borderRadius: 8, padding: 16, fontSize: 13, color: '#aaa', textAlign: 'center' }}>
                        {status === 'connecting' && '⏳ Connexion en cours...'}
                        {status === 'waiting' && '⏳ En attente de connexion du technicien...'}
                        {status === 'connected' && '🟢 Votre écran est partagé avec le technicien'}
                        {status === 'screen_denied' && "⚠️ Vous avez refusé le partage d'écran. Rechargez la page pour réessayer."}
                        {status === 'error' && '🔴 Erreur de connexion. Vérifiez votre connexion internet et rechargez la page.'}
                    </div>
                )}
            </div>

            {/* Chat */}
            <div style={{ width: 260, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ fontSize: 12, color: '#666', margin: 0 }}>Chat</p>
                <div style={{
                    flex: 1, background: '#1a1a1a', borderRadius: 8,
                    padding: 10, minHeight: 200, maxHeight: 300, overflowY: 'auto'
                }}>
                    {messages.length === 0 && <p style={{ color: '#444', fontSize: 12, margin: 0 }}>Aucun message</p>}
                    {messages.map((m, i) => (
                        <div key={i} style={{ marginBottom: 6, fontSize: 13 }}>
                            <span style={{ color: m.from === role ? '#6E56CF' : '#4caf50', fontWeight: 600 }}>
                                {m.from === role ? 'Vous' : m.from} :
                            </span>{' '}
                            <span style={{ color: '#ddd' }}>{m.text}</span>
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    <input
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && sendMessage()}
                        placeholder="Message..."
                        style={{
                            flex: 1, background: '#1a1a1a', border: '1px solid #2a2a2a',
                            borderRadius: 6, padding: '6px 10px', color: '#fff', fontSize: 12
                        }}
                    />
                    <button onClick={sendMessage} style={{
                        background: '#6E56CF', color: '#fff', border: 'none',
                        borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12
                    }}>
                        ↑
                    </button>
                </div>
            </div>
        </div>
    );
}
